/**
 * screens/modals/AssignCashModal
 * ──────────────────────────────────────────────────────────────────────────
 * Put company money in someone's custody.
 *
 * EVERY CHOICE FIELD IS A CHIP ROW, not a dropdown — holder type, scope,
 * instrument, funding source, currency. A native `<select>` needs two clicks to
 * move between fields, renders differently on all three platforms, and on a
 * phone opens a picker sheet over the form. A chip row is one click and the
 * options are visible without opening anything.
 *
 * SCOPE CHANGES THE FORM, because scope changes behaviour. Choosing `purpose`
 * reveals a label, an expiry and an end rule — all of which the assignment
 * needs and none of which mean anything on open cash. This is why scope is a
 * fixed enum and not a configurable option list.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { View } from 'react-native';
import { ChipRow, Input, Modal } from '../../components';
import { parseMajorToMinor } from '../../lib/money';
import { sentenceCase } from '../../lib/format';
import { createAssignment } from '../../services/cash/cash.service';
import { END_RULES, SCOPES, type EndRule, type Scope } from '../../schemas/cash';
import type { ListKind, OptionRow } from '../../schemas/config';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

/** Option rows → chip options. Display name wins; the slug is the value. */
const toChips = (rows: readonly OptionRow[]) =>
  rows
    .filter((row) => row.is_active)
    .map((row) => ({ value: row.value, label: row.display_name || sentenceCase(row.value) }));

export interface AssignCashModalProps {
  /** Pre-selected scope, when the modal was opened from a scoped context. */
  initialScope?: Scope;
}

export function AssignCashModal({ initialScope = 'open' }: AssignCashModalProps) {
  const { closeModal } = useShell();
  const { optionsByKind, policy } = useConfig();

  const [holder, setHolder] = useState('');
  const [holderRef, setHolderRef] = useState('');
  const [holderType, setHolderType] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope>(initialScope);
  const [instrument, setInstrument] = useState<string | null>(null);
  const [funding, setFunding] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string | null>(policy?.base_currency ?? null);
  const [limitText, setLimitText] = useState('');

  // purpose scope only
  const [purposeLabel, setPurposeLabel] = useState('');
  const [purposeExpires, setPurposeExpires] = useState('');
  const [endRule, setEndRule] = useState<EndRule | null>(null);

  const firstOf = (kind: ListKind) => optionsByKind[kind].find((row) => row.is_active)?.value ?? null;

  const chips = useMemo(
    () => ({
      holderType: toChips(optionsByKind.holderType),
      instrument: toChips(optionsByKind.instrument),
      funding: toChips(optionsByKind.funding),
      currency: optionsByKind.currency
        .filter((row) => row.is_active)
        .map((row) => ({ value: row.value, label: row.value })),
    }),
    [optionsByKind],
  );

  const isPurpose = scope === 'purpose';

  const { submitting, run } = useModalSubmit({
    validate: () => {
      if (!holder.trim()) return 'Who is holding it? Give the holder a name.';
      if (!holderRef.trim()) return 'The holder needs a reference.';
      if (!holderType && !firstOf('holderType')) return 'Add a holder type in Settings first.';
      if (!instrument && !firstOf('instrument')) return 'Add an instrument in Settings first.';
      if (!funding && !firstOf('funding')) return 'Add a funding source in Settings first.';
      if (!currency) return 'Pick a currency.';
      if (limitText.trim() && parseMajorToMinor(limitText, currency) === null) {
        return 'That is not a valid limit for this currency.';
      }
      // A purpose assignment without a label and an end rule cannot be closed
      // correctly later — there would be no rule for the unspent balance.
      if (isPurpose && !purposeLabel.trim()) return 'A purpose assignment needs a label.';
      if (isPurpose && !endRule) return 'Say what happens to unspent cash when it closes.';
      return null;
    },
    submit: () => {
      const limit = limitText.trim() && currency ? parseMajorToMinor(limitText, currency) : null;
      return createAssignment({
        holder: holder.trim(),
        holder_ref: holderRef.trim(),
        holder_type: holderType ?? firstOf('holderType')!,
        scope,
        instrument: instrument ?? firstOf('instrument')!,
        funding: funding ?? firstOf('funding')!,
        cur: currency!,
        ...(limit ? { limit_minor: limit } : {}),
        ...(isPurpose
          ? {
              purpose_label: purposeLabel.trim(),
              ...(purposeExpires.trim() ? { purpose_expires_at: purposeExpires.trim() } : {}),
              end_rule: endRule!,
            }
          : {}),
      });
    },
    title: 'Cash assigned',
    fallback: `${holder.trim() || 'The holder'} now holds company cash.`,
  });

  return (
    <Modal
      visible
      title="Assign cash"
      hint="Money placed in one holder's custody. Every movement of it is recorded."
      onClose={closeModal}
      primaryLabel="Assign cash"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Input
        label="Holder"
        required
        value={holder}
        onChangeText={setHolder}
        placeholder="Nikhil S."
      />
      <Input
        label="Holder reference"
        required
        hint="The id in People, or the vehicle / shift / store reference."
        value={holderRef}
        onChangeText={setHolderRef}
        placeholder="ppl_9120"
        monospace
      />

      <ChipRow
        label="Holder type"
        required
        options={chips.holderType}
        value={holderType ?? firstOf('holderType')}
        onChange={setHolderType}
      />

      <ChipRow
        label="Scope"
        required
        hint="Open cash, a purpose wallet with an end rule, or a collection bag."
        options={SCOPES.map((value) => ({ value, label: sentenceCase(value) }))}
        value={scope}
        onChange={setScope}
      />

      <ChipRow
        label="Instrument"
        required
        options={chips.instrument}
        value={instrument ?? firstOf('instrument')}
        onChange={setInstrument}
      />

      <ChipRow
        label="Funding source"
        required
        options={chips.funding}
        value={funding ?? firstOf('funding')}
        onChange={setFunding}
      />

      <ChipRow
        label="Currency"
        required
        hint="This balance is never converted into another currency."
        options={chips.currency}
        value={currency}
        onChange={setCurrency}
      />

      <Input
        label="Limit"
        hint="The target a limit count measures against. Leave blank for no limit."
        value={limitText}
        onChangeText={setLimitText}
        placeholder="0.00"
        prefix={currency ?? undefined}
        numeric
      />

      {/* Revealed by scope, because these fields only mean something on a
          purpose wallet. */}
      {isPurpose && (
        <View>
          <Input
            label="Purpose"
            required
            value={purposeLabel}
            onChangeText={setPurposeLabel}
            placeholder="Site visit, Nov"
          />
          <View style={{ height: 15 }} />
          <Input
            label="Expires"
            hint="ISO date, e.g. 2026-11-30."
            value={purposeExpires}
            onChangeText={setPurposeExpires}
            placeholder="2026-11-30"
            monospace
          />
          <View style={{ height: 15 }} />
          <ChipRow
            label="When it closes"
            required
            options={END_RULES.map((value) => ({
              value,
              label: value === 'return_to_company' ? 'Return to company' : 'Release to holder',
            }))}
            value={endRule}
            onChange={setEndRule}
          />
        </View>
      )}
    </Modal>
  );
}

export default AssignCashModal;
